import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, MagnifyingGlass, Pencil, Trash, Link, Copy, UploadSimple, FileXls, CurrencyDollar, Scales, Share, MapPin, Receipt, Package as PackageIcon, WhatsappLogo, QrCode, Download, X, CaretDown, Trophy } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import PartnerTab from '../components/PartnerTab';
import { isPartnerRole } from '../lib/partners';
import PartnerLeaderboard from '../components/PartnerLeaderboard';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import api, { getCustomers, createCustomer, updateCustomer, deleteCustomer, importCustomers, getPackages } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ClientLimitBanner } from '../components/UpgradeBanner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export default function CustomersPage() {
  const { t, language, tx } = useLanguage();
  const { isAtClientLimit, current_clients, max_clients, refreshPlan } = usePlan();
  const { user } = useAuth();
  const isPartnerViewer = isPartnerRole(user?.role);
  // Always point clients to the live production site, not preview
  const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_URL || 'https://trackmypackage.app';
  const onboardLink = user?.company_id
    ? `${PUBLIC_SITE}/join/${user.company_id}`
    : '';

  const handleCopyOnboardLink = () => {
    if (!onboardLink) return;
    navigator.clipboard.writeText(onboardLink);
    toast.success(tx('Onboarding link copied!', 'Enlace copiado!', 'Lien copié!'));
  };

  const handleShareOnboardLink = async () => {
    if (!onboardLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Sign up to track your packages',
          text: 'Create your free account and track your shipments with us.',
          url: onboardLink,
        });
        return;
      } catch { /* user cancelled */ }
    }
    handleCopyOnboardLink();
  };
  const [customers, setCustomers] = useState([]);
  const [pendingCustomers, setPendingCustomers] = useState([]);
  const [mainTab, setMainTab] = useState('customers'); // 'customers' | 'pending' | partner tag
  const [partners, setPartners] = useState([]);
  const activePartner = partners.find((p) => p.tag === mainTab);
  useEffect(() => {
    if (isPartnerViewer) return;
    api.get('/partners').then((r) => setPartners(r.data || [])).catch(() => setPartners([]));
  }, [isPartnerViewer]);
  const [approvingId, setApprovingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', city: 'San José', state: '', pin_location: '', notes: '',
    price_per_lb: '', price_per_kg: '', flat_fee: ''
  });

  // View Packages modal state
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [customerPackages, setCustomerPackages] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [sendingEmailFor, setSendingEmailFor] = useState(null);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [selectedPkgIds, setSelectedPkgIds] = useState([]);
  const [transportFeeInput, setTransportFeeInput] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [modalPkgTab, setModalPkgTab] = useState('active');

  const fetchCustomerInvoices = async (customerId) => {
    try {
      const r = await api.get('/invoices', { params: { customer_id: customerId, limit: 200 } });
      setCustomerInvoices(r.data || []);
    } catch {
      setCustomerInvoices([]);
    }
  };

  const openCustomerPackages = async (customer) => {
    setViewingCustomer(customer);
    setSelectedPkgIds([]);
    setTransportFeeInput('');
    setLoadingPackages(true);
    setCustomerPackages([]);
    setCustomerInvoices([]);
    try {
      const [pkgsRes] = await Promise.all([
        getPackages({ customer_id: customer.id, limit: 500 }),
        fetchCustomerInvoices(customer.id),
      ]);
      setCustomerPackages(pkgsRes.data || []);
    } catch {
      toast.error(tx('Failed to load packages', 'Error al cargar paquetes', 'Échec du chargement'));
    } finally {
      setLoadingPackages(false);
    }
  };

  const closeCustomerPackages = () => {
    setViewingCustomer(null);
    setCustomerPackages([]);
    setCustomerInvoices([]);
    setSelectedPkgIds([]);
    setTransportFeeInput('');
  };

  const shareInvoiceWhatsApp = (invoice) => {
    if (!viewingCustomer?.phone) {
      toast.error(tx('Customer has no phone on file', 'El cliente no tiene teléfono', 'Aucun téléphone enregistré'));
      return;
    }
    // Normalize phone (digits only — wa.me handles country code prefix)
    const phone = String(viewingCustomer.phone).replace(/\D/g, '');
    if (phone.length < 7) {
      toast.error(tx('Invalid phone number', 'Número inválido', 'Numéro invalide'));
      return;
    }
    const url = `${window.location.origin}/invoice/${invoice.invoice_number}`;
    const lang = (viewingCustomer.preferred_language || 'en').toLowerCase();
    const msg = lang.startsWith('es')
      ? `Hola ${viewingCustomer.name.split(' ')[0]}, aquí está tu factura ${invoice.invoice_number} por $${Number(invoice.total_usd || 0).toFixed(2)}: ${url}`
      : `Hi ${viewingCustomer.name.split(' ')[0]}, here's your invoice ${invoice.invoice_number} for $${Number(invoice.total_usd || 0).toFixed(2)}: ${url}`;
    const wa = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
  };

  const copyInvoiceLink = async (invoiceNumber) => {
    const publicUrl = `${window.location.origin}/invoice/${invoiceNumber}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(tx(`Link copied: ${publicUrl}`, `Enlace copiado: ${publicUrl}`, `Lien copié: ${publicUrl}`));
    } catch {
      toast.error(tx('Failed to copy link', 'Error al copiar', 'Échec'));
    }
  };

  const sendInvoiceEmail = async (invoice) => {
    if (!viewingCustomer?.email) {
      toast.error(tx('Customer has no email on file', 'El cliente no tiene email', 'Aucun email enregistré'));
      return;
    }
    if (!window.confirm(tx(
      `Email invoice ${invoice.invoice_number} to ${viewingCustomer.email}?`,
      `¿Enviar factura ${invoice.invoice_number} a ${viewingCustomer.email}?`,
      `Envoyer la facture ${invoice.invoice_number} à ${viewingCustomer.email} ?`
    ))) return;
    setSendingEmailFor(invoice.id);
    try {
      const r = await api.post(`/invoices/${invoice.id}/send-email`);
      toast.success(tx(
        `Sent to ${r.data.sent_to}`,
        `Enviado a ${r.data.sent_to}`,
        `Envoyé à ${r.data.sent_to}`
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to send email', 'Error al enviar', 'Échec'));
    } finally {
      setSendingEmailFor(null);
    }
  };

  const togglePkgSelection = (pkgId) => {
    setSelectedPkgIds((cur) =>
      cur.includes(pkgId) ? cur.filter((p) => p !== pkgId) : [...cur, pkgId]
    );
  };

  // A package is "invoiceable" if it has NOT been invoiced yet AND it's
  // currently arrived in SJ or ready for pickup. Delivered packages are
  // treated as cash hand-offs already settled — they must NOT appear as
  // invoiceable nor be auto-selected, otherwise the modal total drifts
  // from the customer's recomputed balance.
  const isInvoiceable = (pkg) => {
    return !pkg.invoiced &&
      ['arrived_san_jose', 'ready_for_pickup'].includes(pkg.status);
  };

  const selectableIds = customerPackages.filter(isInvoiceable).map((p) => p.id);
  const allInvoiceableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedPkgIds.includes(id));

  const subtotalSelected = customerPackages
    .filter((p) => selectedPkgIds.includes(p.id))
    .reduce((s, p) => s + (Number(p.shipping_cost) || 0) + (Number(p.transport_fee) || 0), 0);
  const transportFeeNum = parseFloat(transportFeeInput) || 0;
  const totalSelected = subtotalSelected + transportFeeNum;

  const handleCreateInvoiceFromModal = async () => {
    if (!viewingCustomer) return;
    if (selectedPkgIds.length === 0) {
      toast.error(tx('Select at least one package', 'Selecciona al menos un paquete', 'Sélectionnez au moins un colis'));
      return;
    }
    if (!window.confirm(tx(
      `Create invoice for ${selectedPkgIds.length} package(s)${transportFeeNum > 0 ? ` + $${transportFeeNum.toFixed(2)} transport fee` : ''} and email ${viewingCustomer.email}?`,
      `¿Crear factura para ${selectedPkgIds.length} paquete(s)${transportFeeNum > 0 ? ` + $${transportFeeNum.toFixed(2)} de transporte` : ''} y enviar a ${viewingCustomer.email}?`,
      `Créer une facture pour ${selectedPkgIds.length} colis et envoyer à ${viewingCustomer.email} ?`
    ))) return;
    setCreatingInvoice(true);
    try {
      const due = new Date();
      due.setDate(due.getDate() + 7);
      const dueDate = due.toISOString();
      const r = await api.post('/invoices', {
        customer_id: viewingCustomer.id,
        package_ids: selectedPkgIds,
        due_date: dueDate,
        transport_fee: transportFeeNum,
        notes: transportFeeNum > 0 ? `Includes $${transportFeeNum.toFixed(2)} shipping/transport fee` : '',
      });
      // Auto-copy the public invoice link so admin can paste/share immediately
      const publicUrl = `${window.location.origin}/invoice/${r.data.invoice_number}`;
      try { await navigator.clipboard.writeText(publicUrl); } catch { /* ignore */ }
      toast.success(tx(
        `Invoice ${r.data.invoice_number} created — link copied to clipboard`,
        `Factura ${r.data.invoice_number} creada — enlace copiado`,
        `Facture ${r.data.invoice_number} créée — lien copié`
      ));
      closeCustomerPackages();
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to create invoice', 'Error al crear factura', 'Échec de la création'));
    } finally {
      setCreatingInvoice(false);
    }
  };

  // One-click auto-invoice — fetches the customer's eligible (arrived/ready/delivered, uninvoiced)
  // packages and posts an invoice for all of them in one shot.
  const [autoInvoicingId, setAutoInvoicingId] = useState(null);
  const handleAutoInvoice = async (customer, e) => {
    e?.stopPropagation();
    setAutoInvoicingId(customer.id);
    try {
      const res = await getPackages({ customer_id: customer.id, limit: 500 });
      const eligible = (res.data || []).filter(p =>
        !p.invoiced &&
        ['arrived_san_jose', 'ready_for_pickup'].includes(p.status)
      );
      if (eligible.length === 0) {
        toast.info(tx(
          `No arrived/ready uninvoiced packages for ${customer.name}`,
          `No hay paquetes llegados/listos sin facturar para ${customer.name}`,
          `Aucun colis prêt à facturer pour ${customer.name}`
        ));
        return;
      }
      const subtotal = eligible.reduce((s, p) => s + (Number(p.shipping_cost) || 0) + (Number(p.transport_fee) || 0), 0);
      if (!window.confirm(tx(
        `Create invoice for ${eligible.length} package(s) totaling $${subtotal.toFixed(2)} for ${customer.name}? (Link will be copied to clipboard — no email sent)`,
        `¿Crear factura para ${eligible.length} paquete(s) por $${subtotal.toFixed(2)} de ${customer.name}? (Se copia el enlace — sin email)`,
        `Créer une facture pour ${eligible.length} colis ($${subtotal.toFixed(2)}) pour ${customer.name} ? (Lien copié — sans email)`
      ))) return;
      const due = new Date();
      due.setDate(due.getDate() + 7);
      const r = await api.post('/invoices', {
        customer_id: customer.id,
        package_ids: eligible.map(p => p.id),
        due_date: due.toISOString(),
        transport_fee: 0,
        notes: null,
      });
      const publicUrl = `${window.location.origin}/invoice/${r.data.invoice_number}`;
      try { await navigator.clipboard.writeText(publicUrl); } catch { /* ignore */ }
      toast.success(tx(
        `Invoice ${r.data.invoice_number} created ($${subtotal.toFixed(2)}) — link copied to clipboard`,
        `Factura ${r.data.invoice_number} creada ($${subtotal.toFixed(2)}) — enlace copiado`,
        `Facture ${r.data.invoice_number} créée ($${subtotal.toFixed(2)}) — lien copié`
      ), { duration: 7000 });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to auto-invoice', 'Error al facturar automáticamente', 'Échec'));
    } finally {
      setAutoInvoicingId(null);
    }
  };

  useEffect(() => { fetchCustomers(); fetchPending(); }, [search]);

  const fetchCustomers = async () => {
    try {
      const params = search ? { search } : {};
      const response = await getCustomers(params);
      setCustomers(response.data);
    } catch { toast.error(tx('Failed to fetch customers', 'Error al cargar clientes', 'Failed to fetch customers')); }
    finally { setLoading(false); }
  };


  const fetchPending = async () => {
    try {
      const r = await api.get('/customers/pending');
      setPendingCustomers(r.data || []);
    } catch {
      setPendingCustomers([]);
    }
  };

  const handleApprove = async (customerId) => {
    setApprovingId(customerId);
    try {
      await api.post(`/customers/${customerId}/approve`);
      toast.success(tx('Signup approved — welcome email sent', 'Registro aprobado', 'Inscription approuvée'));
      fetchPending();
      fetchCustomers();
      refreshPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to approve', 'Error al aprobar', 'Échec'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (customerId, name) => {
    if (!window.confirm(tx(
      `Reject ${name}'s signup? Their account will be archived and cannot be recovered from this screen.`,
      `¿Rechazar el registro de ${name}? Su cuenta será archivada.`,
      `Rejeter l'inscription de ${name} ?`
    ))) return;
    setApprovingId(customerId);
    try {
      await api.post(`/customers/${customerId}/reject`);
      toast.success(tx('Signup rejected', 'Registro rechazado', 'Inscription rejetée'));
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to reject', 'Error al rechazar', 'Échec'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price_per_lb: formData.price_per_lb ? parseFloat(formData.price_per_lb) : null,
        price_per_kg: formData.price_per_kg ? parseFloat(formData.price_per_kg) : null,
        flat_fee: formData.flat_fee ? parseFloat(formData.flat_fee) : null,
      };
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, payload);
        toast.success(tx('Customer updated', 'Cliente actualizado', 'Client mis à jour'));
      } else {
        await createCustomer(payload);
        toast.success(tx('Customer created', 'Cliente creado', 'Client créé'));
        refreshPlan();
      }
      setShowModal(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Failed to save', 'Error al guardar', 'Failed to save'));
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name, email: customer.email, phone: customer.phone,
      address: customer.address || '', city: customer.city, state: customer.state || '',
      pin_location: customer.pin_location || '',
      notes: customer.notes || '',
      price_per_lb: customer.price_per_lb != null ? String(customer.price_per_lb) : '',
      price_per_kg: customer.price_per_kg != null ? String(customer.price_per_kg) : '',
      flat_fee: customer.flat_fee != null ? String(customer.flat_fee) : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm(tx('Delete this customer?', '¿Eliminar este cliente?', 'Delete this customer?'))) return;
    try {
      await deleteCustomer(customerId);
      toast.success(tx('Customer deleted', 'Cliente eliminado', 'Client supprimé'));
      fetchCustomers();
    } catch { toast.error(tx('Failed to delete', 'Error al eliminar', 'Failed to delete')); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importCustomers(file);
      const d = res.data;
      toast.success(`${d.created} clients imported, ${d.skipped} skipped`);
      if (d.errors?.length) d.errors.slice(0, 3).forEach(err => toast.error(err));
      fetchCustomers();
      refreshPlan();
      setShowImport(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({ name: '', email: '', phone: '', address: '', city: 'San José', state: '', pin_location: '', notes: '', price_per_lb: '', price_per_kg: '', flat_fee: '' });
  };

  return (
    <Layout title={t('customers')}>
      <div className="space-y-6 premium-fade-up" data-testid="customers-page">
        {isAtClientLimit && <ClientLimitBanner current={current_clients} max={max_clients} />}

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 sm:max-w-xs w-full">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder={t('searchCustomers')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-lg" data-testid="customer-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {!isPartnerViewer && (
            <>
            <Button onClick={handleShareOnboardLink} variant="outline" className="rounded-lg border-emerald-400/30 text-emerald-600 hover:bg-emerald-50" data-testid="share-onboard-link-btn" disabled={!onboardLink}>
              <Share size={18} className="mr-2" />
              {tx('Share Signup Link', 'Compartir Enlace', 'Lien d\'Inscription')}
            </Button>
            <Button onClick={() => setShowImport(true)} variant="outline" className="rounded-lg border-[#0077FF]/30 text-[#0077FF] hover:bg-[#0077FF]/5" data-testid="import-customers-btn">
              <UploadSimple size={18} className="mr-2" />
              {tx('Import', 'Importar', 'Importer')}
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="ship-gradient text-white rounded-lg" data-testid="create-customer-btn" disabled={isAtClientLimit}>
              <Plus size={18} className="mr-2" /> {t('newCustomer')}
            </Button>
            </>
            )}
          </div>
        </div>

        {/* Onboarding link card — easy copy/share for tenants */}
        {onboardLink && !isPartnerViewer && (
          <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200/60 rounded-xl p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between" data-testid="onboard-link-card">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                {tx('Your Client Signup Page', 'Tu Página de Registro', 'Votre page d\'inscription')}
              </p>
              <p className="text-xs text-gray-500 mb-2">
                {tx(
                  'Share this link with your clients — they create their own account and start tracking instantly.',
                  'Comparte este enlace con tus clientes — crean su cuenta y empiezan a rastrear al instante.',
                  'Partagez ce lien — vos clients créent leur compte et suivent leurs colis.'
                )}
              </p>
              <code className="text-xs sm:text-sm text-gray-800 bg-white/70 border border-emerald-200/50 rounded-md px-2 py-1 inline-block max-w-full truncate" data-testid="onboard-link-text">
                {onboardLink}
              </code>
            </div>
            <Button onClick={handleCopyOnboardLink} variant="outline" size="sm" className="rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-100 shrink-0" data-testid="copy-onboard-link-btn">
              <Copy size={16} className="mr-2" />
              {tx('Copy', 'Copiar', 'Copier')}
            </Button>
          </div>
        )}

        {/* Main tabs: Customers | Partner Clients (dropdown) | Pending Approvals */}
        {!isPartnerViewer && (
        <div className="inline-flex bg-gray-100 rounded-lg p-1 text-sm font-semibold" data-testid="customers-main-tabs">
          <button
            onClick={() => setMainTab('customers')}
            className={`px-4 py-1.5 rounded-md transition ${mainTab === 'customers' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-customers"
          >
            {tx('Customers', 'Clientes', 'Clients')}
            <span className="ml-2 text-xs text-gray-400">({customers.length})</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`px-4 py-1.5 rounded-md transition inline-flex items-center gap-1.5 focus:outline-none ${activePartner || mainTab === 'leaderboard' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="partner-clients-dropdown"
              >
                {mainTab === 'leaderboard' ? tx('All Partners', 'Todos los Socios', 'Tous les Partenaires') : activePartner ? `${activePartner.name} ${tx('Clients', 'Clientes', 'Clients')}` : tx('Partner Clients', 'Clientes de Socios', 'Clients Partenaires')}
                <CaretDown size={14} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" data-testid="partner-clients-menu">
              <DropdownMenuItem onSelect={() => setMainTab('leaderboard')} className="font-semibold" data-testid="partner-option-leaderboard">
                <Trophy size={15} className="mr-2 text-amber-500" weight="duotone" />
                {tx('All Partners (Leaderboard)', 'Todos los Socios (Ranking)', 'Tous (Classement)')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {partners.map((p) => (
                <DropdownMenuItem key={p.tag} onSelect={() => setMainTab(p.tag)} data-testid={`partner-option-${p.tag}`}>
                  {p.name}
                  {!p.active && <span className="ml-2 text-[10px] uppercase font-bold text-gray-400">{tx('Disabled', 'Desactivado', 'Désactivé')}</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={() => setMainTab('pending')}
            className={`px-4 py-1.5 rounded-md transition relative ${mainTab === 'pending' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-pending-approvals"
          >
            {tx('Pending Approvals', 'Aprobaciones Pendientes', 'En attente')}
            {pendingCustomers.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold rounded-full bg-amber-500 text-white" data-testid="pending-approvals-badge">
                {pendingCustomers.length}
              </span>
            )}
          </button>
        </div>
        )}

        {mainTab === 'leaderboard' && <PartnerLeaderboard onSelect={setMainTab} tx={tx} />}
        {activePartner && (
          <PartnerTab key={activePartner.tag} tag={activePartner.tag} title={`${activePartner.name} ${tx('Clients', 'Clientes', 'Clients')}`} accent={activePartner.accent} companyId={user?.company_id} publicSite={PUBLIC_SITE} isViewer={isPartnerViewer} search={search} tx={tx} />
        )}

        {/* Pending Approvals Panel */}
        {mainTab === 'pending' && (
          <div className="space-y-3" data-testid="pending-approvals-panel">
            {pendingCustomers.length === 0 ? (
              <div className="kpi-card text-center py-10">
                <p className="text-gray-500">{tx('No pending signups right now.', 'No hay registros pendientes.', 'Aucune inscription en attente.')}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {tx('New public-link signups will appear here for approval.', 'Los nuevos registros aparecerán aquí para aprobación.', 'Les nouvelles inscriptions apparaîtront ici.')}
                </p>
              </div>
            ) : (
              pendingCustomers.map((pc) => (
                <div key={pc.id} className="kpi-card flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between" data-testid={`pending-customer-${pc.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                      <span className="text-amber-700 font-bold">{pc.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 truncate">{pc.name}</p>
                      <p className="text-xs text-gray-500 truncate">{pc.email} · {pc.phone || '—'}</p>
                      {(pc.address || pc.city) && (
                        <p className="text-xs text-gray-400 truncate">{[pc.address, pc.city].filter(Boolean).join(', ')}</p>
                      )}
                      <p className="text-[10px] text-amber-600 mt-0.5 uppercase tracking-wide font-bold">
                        {tx('Signed up', 'Registrado', 'Inscrit')} · {new Date(pc.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={() => handleApprove(pc.id)}
                      disabled={approvingId === pc.id}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                      data-testid={`approve-customer-${pc.id}`}
                    >
                      {approvingId === pc.id ? '…' : tx('Approve', 'Aprobar', 'Approuver')}
                    </Button>
                    <Button
                      onClick={() => handleReject(pc.id, pc.name)}
                      disabled={approvingId === pc.id}
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-600 hover:bg-rose-50 rounded-lg"
                      data-testid={`reject-customer-${pc.id}`}
                    >
                      {tx('Reject', 'Rechazar', 'Rejeter')}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Customers Grid */}
        {mainTab === 'customers' && (
          loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : customers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customers.map((customer, index) => (
              <motion.div
                key={customer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => openCustomerPackages(customer)}
                className="kpi-card cursor-pointer hover:shadow-lg transition-shadow"
                data-testid={`customer-card-${customer.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl ship-gradient flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{customer.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                      <p className="text-sm text-gray-500">{[customer.city, customer.state].filter(Boolean).join(', ')}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!isPartnerViewer && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(customer); }} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid={`edit-customer-${customer.id}`}>
                          <Pencil size={16} className="text-gray-600" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(customer.id); }} className="p-1.5 hover:bg-red-50 rounded-lg">
                          <Trash size={16} className="text-red-600" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5 text-sm">
                  <p className="text-gray-600">{customer.email}</p>
                  <p className="text-gray-600">{customer.phone}</p>
                  {customer.address && <p className="text-gray-500">{customer.address}</p>}
                  {customer.pin_location && (
                    <a
                      href={customer.pin_location}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 hover:underline"
                      data-testid={`customer-pin-link-${customer.id}`}
                    >
                      <MapPin size={12} weight="fill" />
                      {tx('Open pin location', 'Abrir ubicación', 'Voir l\'emplacement')}
                    </a>
                  )}
                  {customer.tracking_link_id && (
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <a
                        href={`/portal/submit/${customer.tracking_link_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
                        data-testid={`customer-tracking-upload-${customer.id}`}
                        title={tx('Open client tracking upload page', 'Abrir página de carga de tracking', 'Page de saisie tracking')}
                      >
                        <UploadSimple size={11} weight="bold" />
                        {tx('Enter Tracking', 'Ingresar Tracking', 'Saisir Tracking')}
                      </a>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/portal/submit/${customer.tracking_link_id}`;
                          navigator.clipboard.writeText(url);
                          toast.success(tx('Upload link copied', 'Enlace copiado', 'Lien copié'));
                        }}
                        className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700"
                        data-testid={`customer-tracking-copy-${customer.id}`}
                        title={tx('Copy the client\'s upload link', 'Copiar enlace del cliente', 'Copier le lien du client')}
                      >
                        <Copy size={10} />
                        {tx('Copy link', 'Copiar enlace', 'Copier')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Pricing badges */}
                {(customer.price_per_lb || customer.price_per_kg || customer.flat_fee) && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {customer.price_per_lb != null && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                        <Scales size={10} /> ${customer.price_per_lb}/lb
                      </span>
                    )}
                    {customer.price_per_kg != null && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                        <Scales size={10} /> ${customer.price_per_kg}/kg
                      </span>
                    )}
                    {customer.flat_fee != null && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                        <CurrencyDollar size={10} /> ${customer.flat_fee} flat
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs uppercase tracking-wide text-gray-500">{t('totalPackagesLabel')}</span>
                    <p className="font-bold text-[#0077FF]">{customer.total_packages}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs uppercase tracking-wide text-gray-500">{t('balance')}</span>
                    <div className="flex items-center justify-end gap-1">
                      <p className="font-bold text-[#FFB300]">${customer.balance?.toFixed(2) || '0.00'}</p>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const r = await api.post(`/customers/${customer.id}/recompute-balance`);
                            toast.success(tx(
                              `Balance fixed: $${r.data.old_balance.toFixed(2)} → $${r.data.new_balance.toFixed(2)}`,
                              `Saldo corregido: $${r.data.old_balance.toFixed(2)} → $${r.data.new_balance.toFixed(2)}`,
                              `Solde corrigé`
                            ));
                            fetchCustomers();
                          } catch (err) {
                            toast.error(tx('Failed to recompute', 'Error al recalcular', 'Échec'));
                          }
                        }}
                        title={tx('Recompute balance from invoices + packages', 'Recalcular saldo desde facturas y paquetes', 'Recalculer le solde')}
                        className="p-0.5 hover:bg-amber-50 rounded"
                        data-testid={`recompute-balance-${customer.id}`}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tracking Link */}
                {customer.tracking_link_id && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Link size={14} className="text-[#0077FF]" />
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{tx('Client Portal Link', 'Enlace del Portal', 'Lien du Portail')}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <code className="text-[10px] bg-gray-50 px-2 py-1 rounded font-mono text-gray-600 flex-1 truncate">
                        {window.location.origin}/portal/submit/{customer.tracking_link_id}
                      </code>
                      <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/portal/submit/${customer.tracking_link_id}`); toast.success(tx('Link copied!', 'Enlace copiado!', 'Lien copie !')); }}
                        className="p-1 hover:bg-gray-100 rounded" data-testid={`copy-link-${customer.id}`}>
                        <Copy size={14} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}

                {/* View Packages & Invoice Button */}
                <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomerPackages(customer);
                    }}
                    className="ship-gradient text-white rounded-lg text-xs"
                    data-testid={`view-packages-${customer.id}`}
                  >
                    <PackageIcon size={14} weight="bold" className="mr-1.5" />
                    {tx('View & Invoice', 'Ver y Facturar', 'Voir & Facturer')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => handleAutoInvoice(customer, e)}
                    disabled={autoInvoicingId === customer.id}
                    className="rounded-lg text-xs border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                    data-testid={`auto-invoice-${customer.id}`}
                    title={tx('Create invoice from all arrived/ready uninvoiced packages', 'Crear factura con todos los paquetes llegados sin facturar', 'Facture auto pour les colis arrivés')}
                  >
                    <Receipt size={14} weight="bold" className="mr-1.5" />
                    {autoInvoicingId === customer.id
                      ? tx('Creating…', 'Creando…', 'Création…')
                      : tx('Auto-Invoice', 'Factura Auto', 'Facture Auto')}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="kpi-card text-center py-12">
            <Users size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-600">{t('noCustomers')}</p>
            <p className="text-sm text-gray-500 mt-1">
              {isPartnerViewer
                ? tx('Clients who sign up with your link will appear here.', 'Los clientes que se registren con tu enlace aparecerán aquí.', 'Les clients inscrits via votre lien apparaîtront ici.')
                : t('addFirstCustomer')}
            </p>
          </motion.div>
        ))}

        {/* ══ Client Packages / Create Invoice Modal ══ */}
        <Dialog open={!!viewingCustomer} onOpenChange={(o) => { if (!o) closeCustomerPackages(); }}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="customer-packages-modal">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-bold tracking-wide flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
                    <PackageIcon size={22} className="text-[#0077FF]" weight="fill" />
                    {viewingCustomer?.name} — {tx('Packages', 'Paquetes', 'Colis')}
                  </DialogTitle>
                  <p className="text-xs text-gray-500 mt-1">{viewingCustomer?.email} · {viewingCustomer?.phone}</p>
                </div>
                {viewingCustomer?.id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await api.get(`/customers/${viewingCustomer.id}/ship-label.pdf`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = url;
                        const safeName = (viewingCustomer.name || 'client').replace(/\s+/g, '_');
                        link.setAttribute('download', `ship-label-${safeName}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success(tx(`Label downloaded for ${viewingCustomer.name}`, `Etiqueta descargada para ${viewingCustomer.name}`, `Étiquette téléchargée pour ${viewingCustomer.name}`));
                      } catch (err) {
                        toast.error(err.response?.data?.detail || tx('Failed to download label', 'Error al descargar', 'Échec'));
                      }
                    }}
                    className="rounded-lg text-xs shrink-0 border-[#0077FF] text-[#0077FF] hover:bg-blue-50"
                    data-testid={`download-label-${viewingCustomer.id}`}
                    title={tx('Download this client\'s Miami ship-to label PDF', 'Descargar la etiqueta Miami de este cliente', 'Télécharger l\'étiquette Miami')}
                  >
                    <MapPin size={14} weight="bold" className="mr-1.5" />
                    {tx('Ship-To Label', 'Etiqueta Envío', 'Étiquette')}
                  </Button>
                )}
              </div>
            </DialogHeader>

            {loadingPackages ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : customerPackages.length === 0 ? (
              <div className="text-center py-12">
                <PackageIcon size={40} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">{tx('No packages for this client yet', 'Sin paquetes para este cliente', 'Aucun colis pour ce client')}</p>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {/* Active / Completed tabs */}
                <div className="inline-flex bg-gray-100 rounded-lg p-1 text-xs font-bold" data-testid="customer-pkg-tab-filter">
                  {[
                    { id: 'active', label: tx('Active', 'Activos', 'Actifs') },
                    { id: 'completed', label: tx('Completed', 'Completados', 'Complétés') },
                  ].map(o => (
                    <button
                      key={o.id}
                      onClick={() => { setModalPkgTab(o.id); setSelectedPkgIds([]); }}
                      className={`px-3 py-1.5 rounded-md transition-colors ${modalPkgTab === o.id ? 'bg-white text-[#0077FF] shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                      data-testid={`customer-modal-pkg-tab-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>

                {/* Selection helper bar (active only) */}
                {modalPkgTab === 'active' && (
                <div className="flex items-center justify-between bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allInvoiceableSelected}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedPkgIds(selectableIds);
                        else setSelectedPkgIds([]);
                      }}
                      data-testid="select-all-pkgs-checkbox"
                    />
                    {tx('Select all unbilled arrived/ready packages', 'Seleccionar todos los paquetes llegados sin facturar', 'Sélectionner tous les colis non facturés')}
                    {selectableIds.length > 0 && <span className="text-gray-400">({selectableIds.length})</span>}
                  </label>
                  <span className="text-xs text-gray-500">{customerPackages.length} {tx('total', 'total', 'au total')}</span>
                </div>
                )}

                {/* Packages table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-2 py-2 w-8"></th>
                        <th className="px-2 py-2 text-left">{tx('Tracking', 'Tracking', 'Tracking')}</th>
                        <th className="px-2 py-2 text-left">{tx('Description', 'Descripción', 'Description')}</th>
                        <th className="px-2 py-2 text-right">{tx('Weight', 'Peso', 'Poids')}</th>
                        <th className="px-2 py-2 text-right">{tx('Cost', 'Costo', 'Coût')}</th>
                        <th className="px-2 py-2 text-center">{tx('Status', 'Estado', 'Statut')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerPackages
                        .filter(p => (modalPkgTab === 'completed' ? p.status === 'delivered' : p.status !== 'delivered'))
                        .map((p) => {
                        const canInvoice = isInvoiceable(p);
                        const isChecked = selectedPkgIds.includes(p.id);
                        const cost = (Number(p.shipping_cost) || 0) + (Number(p.transport_fee) || 0);
                        return (
                          <tr
                            key={p.id}
                            className={`border-t ${isChecked ? 'bg-blue-50/50' : ''} ${canInvoice ? 'hover:bg-gray-50' : 'opacity-60'}`}
                            data-testid={`pkg-row-${p.id}`}
                          >
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={!canInvoice}
                                onChange={() => togglePkgSelection(p.id)}
                                data-testid={`pkg-checkbox-${p.id}`}
                              />
                            </td>
                            <td className="px-2 py-2 font-mono text-[11px] text-gray-700">{p.tracking_number}</td>
                            <td className="px-2 py-2 text-gray-700">{p.description || '—'}</td>
                            <td className="px-2 py-2 text-right text-gray-700">
                              {p.weight_kg ? `${p.weight_kg} kg` : (p.weight_lbs ? `${p.weight_lbs} lb` : '—')}
                            </td>
                            <td className="px-2 py-2 text-right font-semibold text-[#0077FF]">${cost.toFixed(2)}</td>
                            <td className="px-2 py-2 text-center">
                              <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {p.status}
                              </span>
                              {p.invoiced && (
                                <span className="ml-1 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                  {tx('INVOICED', 'FACTURADO', 'FACTURÉ')}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Invoice builder (active tab only) */}
                {modalPkgTab === 'active' && (
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-4">
                  <p className="text-[11px] uppercase tracking-wider text-amber-700 font-bold mb-3">
                    {tx('Create Invoice', 'Crear Factura', 'Créer la Facture')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-gray-700">
                        {tx('Shipping / Transport Fee (USD)', 'Tarifa de Envío / Transporte (USD)', 'Frais de transport (USD)')}
                      </Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={transportFeeInput}
                          onChange={(e) => setTransportFeeInput(e.target.value)}
                          placeholder="0.00"
                          className="pl-7 rounded-lg"
                          data-testid="modal-transport-fee-input"
                        />
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {tx('Optional flat fee added to the invoice', 'Tarifa adicional opcional', 'Frais forfaitaire optionnel')}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-amber-200 p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">{tx('Selected', 'Seleccionados', 'Sélectionnés')}</span>
                        <span className="font-semibold">{selectedPkgIds.length} {tx('pkgs', 'paq.', 'colis')}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-500">{tx('Subtotal', 'Subtotal', 'Sous-total')}</span>
                        <span className="font-semibold">${subtotalSelected.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-500">{tx('Transport', 'Transporte', 'Transport')}</span>
                        <span className="font-semibold">${transportFeeNum.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-2 pt-2 border-t border-amber-200">
                        <span className="font-bold text-gray-800">{tx('Total', 'Total', 'Total')}</span>
                        <span className="font-bold text-[#0077FF] text-lg" data-testid="modal-total-usd">${totalSelected.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button type="button" variant="outline" onClick={closeCustomerPackages} className="rounded-lg">
                      {t('cancel')}
                    </Button>
                    <Button
                      type="button"
                      disabled={creatingInvoice || selectedPkgIds.length === 0}
                      onClick={handleCreateInvoiceFromModal}
                      className="ship-gradient text-white rounded-lg"
                      data-testid="create-invoice-from-modal-btn"
                    >
                      <Receipt size={14} weight="bold" className="mr-1.5" />
                      {creatingInvoice
                        ? tx('Creating…', 'Creando…', 'Création…')
                        : tx('Create Invoice & Copy Link', 'Crear Factura y Copiar Enlace', 'Créer & Copier Lien')}
                    </Button>
                  </div>
                </div>
                )}

                {/* ── Existing Invoices for this customer (with Copy/Share links) ── */}
                {customerInvoices.length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-3 flex items-center gap-2">
                      <Receipt size={13} weight="bold" className="text-[#0077FF]" />
                      {tx('Invoices', 'Facturas', 'Factures')} · {customerInvoices.length}
                    </p>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {customerInvoices.map((inv) => {
                        const statusColor = inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                             inv.status === 'cancelled' ? 'bg-gray-200 text-gray-500' :
                                             inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                             'bg-amber-100 text-amber-700';
                        return (
                          <div key={inv.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-gray-100 bg-gray-50/40 hover:bg-gray-50">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-bold">{inv.invoice_number}</span>
                                <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${statusColor}`}>
                                  {inv.status}
                                </span>
                              </div>
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                ${Number(inv.total_usd || 0).toFixed(2)} · {(inv.package_ids || []).length} {tx('pkgs', 'paq.', 'colis')}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => copyInvoiceLink(inv.invoice_number)}
                                className="p-1.5 rounded-md hover:bg-blue-50 text-[#0077FF] border border-blue-200"
                                title={tx('Copy public invoice link', 'Copiar enlace público', 'Copier le lien')}
                                data-testid={`copy-invoice-link-${inv.invoice_number}`}
                              >
                                <Copy size={13} />
                              </button>
                              <a
                                href={`/invoice/${inv.invoice_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 border border-gray-200"
                                title={tx('Open public view', 'Abrir vista pública', 'Ouvrir la vue publique')}
                                data-testid={`open-invoice-${inv.invoice_number}`}
                              >
                                <Link size={13} />
                              </a>
                              {inv.status !== 'cancelled' && viewingCustomer?.phone && (
                                <button
                                  type="button"
                                  onClick={() => shareInvoiceWhatsApp(inv)}
                                  className="p-1.5 rounded-md hover:bg-green-50 text-green-700 border border-green-200"
                                  title={tx(`WhatsApp ${viewingCustomer.phone}`, `WhatsApp ${viewingCustomer.phone}`, `WhatsApp ${viewingCustomer.phone}`)}
                                  data-testid={`whatsapp-invoice-${inv.invoice_number}`}
                                >
                                  <WhatsappLogo size={13} weight="bold" />
                                </button>
                              )}
                              {inv.status !== 'cancelled' && viewingCustomer?.email && (
                                <button
                                  type="button"
                                  onClick={() => sendInvoiceEmail(inv)}
                                  disabled={sendingEmailFor === inv.id}
                                  className="px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide hover:bg-emerald-50 text-emerald-700 border border-emerald-200 disabled:opacity-50"
                                  title={tx(`Email to ${viewingCustomer.email}`, `Enviar a ${viewingCustomer.email}`, `Envoyer à ${viewingCustomer.email}`)}
                                  data-testid={`send-invoice-email-${inv.invoice_number}`}
                                >
                                  {sendingEmailFor === inv.id
                                    ? tx('Sending…', 'Enviando…', 'Envoi…')
                                    : tx('Email', 'Email', 'Email')}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ══ Import Modal ══ */}
        <Dialog open={showImport} onOpenChange={setShowImport}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Import Clients', 'Importar Clientes', 'Importer des Clients')}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">{tx('File Format', 'Formato de Archivo', 'Format de Fichier')}</p>
                <p className="text-xs text-blue-600">
                  {tx(
                    'Upload a CSV or Excel (.xlsx) file with columns: Name, Email, Phone, Address, City, State, Notes',
                    'Sube un archivo CSV o Excel (.xlsx) con columnas: Name, Email, Phone, Address, City, State, Notes',
                    'Telechargez un fichier CSV ou Excel (.xlsx) avec les colonnes: Name, Email, Phone, Address, City, State, Notes'
                  )}
                </p>
                <p className="text-xs text-blue-500 mt-1">{tx('Name and Email are required. Pricing defaults to your tenant settings.', 'Nombre y Email son obligatorios.', 'Nom et Email sont obligatoires.')}</p>
              </div>

              <div
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#0077FF] hover:bg-blue-50/30 transition-all"
                onClick={() => fileInputRef.current?.click()}
                data-testid="import-dropzone"
              >
                <FileXls size={40} className="mx-auto mb-3 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">{tx('Click to upload', 'Haz clic para subir', 'Cliquez pour telecharger')}</p>
                <p className="text-xs text-gray-500 mt-1">.csv, .xlsx</p>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
              </div>

              {importing && (
                <div className="flex items-center justify-center gap-3 py-4">
                  <div className="w-5 h-5 border-2 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-gray-600">{tx('Importing...', 'Importando...', 'Importation...')}</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>


        {/* ══ Create/Edit Modal ══ */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {editingCustomer ? tx('Edit Customer', 'Editar Cliente', 'Modifier le Client') : t('newCustomer')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">{t('fullName')}</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Juan Perez" className="rounded-lg" required data-testid="customer-name-input" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('email')}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="juan@email.com" className="rounded-lg" required />
                </div>
                <div>
                  <Label className="form-label">{t('phone')}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+506 8888-1234" className="rounded-lg" required />
                </div>
              </div>

              <div>
                <Label className="form-label">{t('address')}</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder={tx('Full address', 'Direccion completa', 'Full address')} className="rounded-lg" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('city')}</Label>
                  <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="San Jose" className="rounded-lg" required />
                </div>
                <div>
                  <Label className="form-label">{tx('State', 'Estado', 'Etat')}</Label>
                  <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} placeholder={tx('State/Province', 'Estado/Provincia', 'Etat/Province')} className="rounded-lg" />
                </div>
              </div>

              <div>
                <Label className="form-label flex items-center gap-1.5">
                  <MapPin size={14} className="text-rose-500" weight="fill" />
                  {tx('Pin Drop Location (Google Maps link)', 'Ubicación (Enlace de Google Maps)', 'Pin Google Maps')}
                </Label>
                <Input
                  value={formData.pin_location}
                  onChange={(e) => setFormData({ ...formData, pin_location: e.target.value })}
                  placeholder="https://maps.app.goo.gl/... or 9.9281,-84.0907"
                  className="rounded-lg"
                  data-testid="customer-pin-location-input"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {tx('Open Google Maps → Share → Copy link, then paste here.', 'Abre Google Maps → Compartir → Copiar enlace y pégalo aquí.', 'Ouvrez Google Maps → Partager → Copier le lien.')}
                </p>
              </div>

              <div>
                <Label className="form-label">{t('notes')}</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder={tx('Additional notes...', 'Notas adicionales...', 'Additional notes...')} className="rounded-lg" />
              </div>

              {/* Custom Pricing Section */}
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <CurrencyDollar size={18} className="text-[#FF9900]" />
                  <Label className="text-sm font-bold text-gray-800">{tx('Custom Pricing', 'Precios Personalizados', 'Tarification Personnalisee')}</Label>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {tx(
                    'Default is $14.00/kg with a $10.00 flat fee for packages 0.5 kg or under. Set Per Kilo to override the rate for this client (e.g. $15.00).',
                    'Por defecto $14.00/kg con tarifa fija de $10.00 para paquetes de 0.5 kg o menos. Define Por Kilo para personalizar este cliente (ej. $15.00).',
                    'Par défaut 14,00 $/kg avec un forfait de 10 $ pour les colis ≤ 0,5 kg. Définissez Par Kilo pour personnaliser ce client.'
                  )}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500 font-semibold">{tx('Per Kilo', 'Por Kilo', 'Par Kilo')} ★</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input type="number" step="0.01" min="0" value={formData.price_per_kg} onChange={(e) => setFormData({...formData, price_per_kg: e.target.value})} placeholder="14.00" className="pl-7 rounded-lg text-sm border-amber-300" data-testid="price-per-kg" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">{tx('Per Pound', 'Por Libra', 'Par Livre')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input type="number" step="0.01" min="0" value={formData.price_per_lb} onChange={(e) => setFormData({...formData, price_per_lb: e.target.value})} placeholder="—" className="pl-7 rounded-lg text-sm" data-testid="price-per-lb" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">{tx('Flat Fee', 'Tarifa Fija', 'Forfait')}</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input type="number" step="0.01" min="0" value={formData.flat_fee} onChange={(e) => setFormData({...formData, flat_fee: e.target.value})} placeholder="—" className="pl-7 rounded-lg text-sm" data-testid="flat-fee" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-lg">{t('cancel')}</Button>
                <Button type="submit" className="ship-gradient text-white rounded-lg" data-testid="submit-customer-btn">
                  {editingCustomer ? t('update') : t('create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
